const ANSWER_ALIASES = {
  identity: {
    'لها': 'For Her',
    'له': 'For Him',
    'للجميع': 'Unisex',
    'لا تفضيل': 'No preference',
  },
  family: {
    'منعش وحمضي': 'fresh-citrus',
    'مائي وبحري': 'aquatic-marine',
    'زهري': 'floral',
    'فاكهي': 'fruity',
    'حلو وغورماند': 'sweet-gourmand',
    'خشبي': 'woody',
    'عنبري وحار': 'amber-spicy',
    'عود وجلدي': 'oud-leather',
  },
  character: {
    'منعش ونظيف': 'fresh-clean',
    'أنيق وراقٍ': 'elegant-refined',
    'دافئ وحسي': 'warm-sensual',
    'حلو ومرح': 'sweet-playful',
    'جريء وقوي الحضور': 'bold-commanding',
    'ناعم وحميم': 'soft-intimate',
  },
  occasion: {
    'يومي': 'everyday-signature',
    'العمل والاجتماعات': 'office-business',
    'موعد خاص': 'date-night',
    'المساء والمناسبات': 'evening-events',
    'الصيف والسفر': 'summer-travel',
    'هدية': 'gifts',
  },
  season: {
    'الربيع': 'Spring',
    'الصيف': 'Summer',
    'الخريف': 'Autumn',
    'الشتاء': 'Winter',
    'طوال العام': 'All year',
  },
};

function alias(group, value) {
  return ANSWER_ALIASES[group][value] ?? value;
}

export function canonicalAnswerValue(name, value) {
  const group = name === 'families' ? 'family' : name;
  return alias(group, value);
}

export function normalizeAnswers(answers) {
  return {
    identity: alias('identity', answers.identity ?? ''),
    families: (answers.families ?? []).map((value) => alias('family', value)),
    character: alias('character', answers.character ?? ''),
    occasion: alias('occasion', answers.occasion ?? ''),
    season: alias('season', answers.season ?? ''),
  };
}

export function scoreProduct(product, rawAnswers) {
  const answers = normalizeAnswers(rawAnswers);
  const reasons = [];
  let score = product.priority ?? 0;

  if (answers.identity === product.identity) {
    score += 5;
    reasons.push({ type: 'identity', value: product.identity });
  } else if (answers.identity === 'No preference' && product.identity === 'Unisex') {
    score += 1;
  }

  for (const family of answers.families) {
    if (product.families.includes(family)) {
      score += 5;
      reasons.push({ type: 'family', value: family });
    }
  }

  if (answers.occasion && product.occasions.includes(answers.occasion)) {
    score += 4;
    reasons.push({ type: 'occasion', value: answers.occasion });
  }

  if (answers.character && product.characters.includes(answers.character)) {
    score += 3;
    reasons.push({ type: 'character', value: answers.character });
  }

  if (answers.season && answers.season !== 'All year') {
    if (product.seasons.includes(answers.season)) {
      score += 2;
      reasons.push({ type: 'season', value: answers.season });
    } else if (product.seasons.includes('All year')) {
      score += 1;
    }
  }

  return { ...product, score: Number(score.toFixed(1)), reasons };
}

export function rankProducts(products, answers) {
  const unique = new Map();

  for (const product of products) {
    const scored = scoreProduct(product, answers);
    const current = unique.get(product.handle);
    if (!current || scored.score > current.score) unique.set(product.handle, scored);
  }

  return [...unique.values()].sort((a, b) => (
    b.score - a.score
    || (b.priority ?? 0) - (a.priority ?? 0)
    || a.handle.localeCompare(b.handle)
  ));
}

export function selectFamily(currentValues, value, maximum = 2) {
  if (currentValues.includes(value)) {
    return {
      values: currentValues.filter((current) => current !== value),
      limitReached: false,
    };
  }

  if (currentValues.length >= maximum) {
    return { values: [...currentValues], limitReached: true };
  }

  return { values: [...currentValues, value], limitReached: false };
}

export function canContinue(question, answers) {
  if (question === 'families') {
    return answers.families.length >= 1 && answers.families.length <= 2;
  }

  return Boolean(answers[question]);
}

export class FinderProductError extends Error {
  constructor(message, cause) {
    super(message, { cause });
    this.name = 'FinderProductError';
    this.retryable = true;
  }
}

export function normalizeProduct(product, candidate, rank, routeRoot = '/') {
  const availableVariants = (product.variants ?? []).filter((variant) => variant.available);
  if (!product.available || availableVariants.length === 0) return null;

  const price = Math.min(...availableVariants.map((variant) => Number(variant.price)));
  const root = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
  const featuredImage = typeof product.featured_image === 'string'
    ? product.featured_image
    : product.featured_image?.src;

  return {
    handle: product.handle,
    title: product.title,
    url: `${root}products/${product.handle}`,
    image: featuredImage ?? product.images?.[0] ?? '',
    price,
    available: true,
    score: candidate.score,
    reasons: candidate.reasons,
    rank,
  };
}

export async function fetchAvailableResults(
  ranked,
  routeRoot = '/',
  fetchImpl = fetch,
  { maximum = 5, concurrency = 3 } = {},
) {
  const root = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
  const results = [];

  for (let index = 0; index < ranked.length && results.length < maximum; index += concurrency) {
    const candidates = ranked.slice(index, index + concurrency);
    let batch;

    try {
      batch = await Promise.all(candidates.map(async (candidate) => {
        const response = await fetchImpl(`${root}products/${encodeURIComponent(candidate.handle)}.js`);
        if (!response.ok) {
          throw new Error(`Shopify product request failed with status ${response.status ?? 'unknown'}.`);
        }
        return { candidate, product: await response.json() };
      }));
    } catch (error) {
      throw new FinderProductError('Unable to load live Shopify product data.', error);
    }

    for (const { candidate, product } of batch) {
      const normalized = normalizeProduct(product, candidate, results.length + 1, root);
      if (normalized) results.push(normalized);
      if (results.length === maximum) break;
    }
  }

  return results;
}

const EVENT_PAYLOAD_KEYS = {
  scent_finder_start: [],
  scent_finder_step_complete: ['step'],
  scent_finder_complete: [],
  scent_finder_result_view: ['handles'],
  scent_finder_product_click: ['handle', 'rank'],
  scent_finder_retake: [],
  scent_finder_travel_set_click: [],
};

export function emitFinderEvent(name, payload = {}, dataLayer) {
  if (!Array.isArray(dataLayer) || !Object.hasOwn(EVENT_PAYLOAD_KEYS, name)) return false;

  const event = { event: name };
  for (const key of EVENT_PAYLOAD_KEYS[name]) {
    if (payload[key] !== undefined) event[key] = payload[key];
  }
  dataLayer.push(event);
  return true;
}

export function interpolate(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`[${key}]`, String(value)),
    template,
  );
}

if (
  typeof window !== 'undefined'
  && typeof document !== 'undefined'
  && 'customElements' in window
  && !window.customElements.get('scent-finder')
) {
  class NicheScentFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-finder-form]');
      this.questions = [...this.querySelectorAll('[data-question]')];
      this.progress = this.querySelector('[role="progressbar"]');
      this.progressText = this.querySelector('[data-finder-progress-text]');
      this.progressBar = this.querySelector('[data-finder-progress-bar]');
      this.backButton = this.querySelector('[data-finder-back]');
      this.continueButton = this.querySelector('[data-finder-continue]');
      this.loading = this.querySelector('[data-finder-loading]');
      this.error = this.querySelector('[data-finder-error]');
      this.results = this.querySelector('[data-finder-results]');
      this.resultGrid = this.querySelector('[data-finder-result-grid]');
      this.resultTemplate = this.querySelector('[data-finder-result-template]');
      this.status = this.querySelector('[data-finder-status]');
      this.familyMessage = this.querySelector('[data-family-limit-message]');
      this.config = JSON.parse(this.querySelector('[data-finder-config]').textContent);
      this.routeRoot = this.dataset.routeRoot || '/';
      this.currency = this.dataset.currency || 'EGP';
      this.currentStep = 0;
      this.started = false;
      this.answers = {
        identity: '',
        families: [],
        character: '',
        occasion: '',
        season: '',
      };
      this.answerLabels = new Map();

      for (const input of this.querySelectorAll('input')) {
        const label = input.closest('label')?.querySelector('span')?.textContent?.trim();
        if (label) this.answerLabels.set(canonicalAnswerValue(input.name, input.value), label);
      }

      this.form.addEventListener('change', (event) => this.handleAnswer(event));
      this.backButton.addEventListener('click', () => this.showStep(this.currentStep - 1));
      this.continueButton.addEventListener('click', () => this.continue());
      this.querySelector('[data-finder-retry]').addEventListener('click', () => this.loadResults());
      this.querySelector('[data-finder-retake]').addEventListener('click', () => this.retake());
      this.querySelector('[data-finder-travel]').addEventListener('click', () => {
        emitFinderEvent('scent_finder_travel_set_click', {}, window.dataLayer);
      });
      this.showStep(0, false);
    }

    handleAnswer(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (!this.started) {
        this.started = true;
        emitFinderEvent('scent_finder_start', {}, window.dataLayer);
      }

      if (input.name === 'families') {
        const selection = selectFamily(this.answers.families, input.value);
        if (selection.limitReached) input.checked = false;
        this.answers.families = selection.values;
        this.familyMessage.hidden = !selection.limitReached;
        if (selection.limitReached) this.status.textContent = this.config.familyLimitMessage;
      } else {
        this.answers[input.name] = input.value;
      }

      this.updateContinueState();
    }

    updateContinueState() {
      const question = this.questions[this.currentStep].dataset.question;
      this.continueButton.disabled = !canContinue(question, this.answers);
    }

    showStep(index, focus = true) {
      if (index < 0 || index >= this.questions.length) return;
      this.currentStep = index;
      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== index;
      });
      this.backButton.hidden = index === 0;
      this.familyMessage.hidden = true;
      this.progress.setAttribute('aria-valuenow', String(index + 1));
      this.progressText.textContent = interpolate(this.config.progressFormat, {
        current: index + 1,
        total: this.questions.length,
      });
      this.progressBar.style.inlineSize = `${((index + 1) / this.questions.length) * 100}%`;
      this.status.textContent = interpolate(this.config.stepStatus, {
        current: index + 1,
        total: this.questions.length,
      });
      this.updateContinueState();
      if (focus) this.questions[index].querySelector('legend')?.focus();
    }

    continue() {
      const question = this.questions[this.currentStep].dataset.question;
      if (!canContinue(question, this.answers)) return;
      emitFinderEvent('scent_finder_step_complete', { step: this.currentStep + 1 }, window.dataLayer);

      if (this.currentStep < this.questions.length - 1) {
        this.showStep(this.currentStep + 1);
        return;
      }

      emitFinderEvent('scent_finder_complete', {}, window.dataLayer);
      this.loadResults();
    }

    async loadResults() {
      this.form.hidden = true;
      this.progress.hidden = true;
      this.error.hidden = true;
      this.results.hidden = true;
      this.loading.hidden = false;

      try {
        const products = window.NicheScentFinderData?.products;
        if (!Array.isArray(products) || products.length !== 62) {
          throw new FinderProductError('The recommendation index is unavailable.');
        }
        const ranked = rankProducts(products, this.answers);
        const available = await fetchAvailableResults(
          ranked,
          this.routeRoot,
          window.fetch.bind(window),
        );
        this.renderResults(available);
      } catch (error) {
        this.loading.hidden = true;
        this.error.hidden = false;
        this.status.textContent = this.querySelector('[data-finder-error-text]').textContent;
      }
    }

    renderResults(products) {
      this.resultGrid.replaceChildren();
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      });

      for (const product of products) {
        const card = this.resultTemplate.content.firstElementChild.cloneNode(true);
        const primaryLink = card.querySelector('[data-result-link]');
        const cta = card.querySelector('[data-result-cta]');
        const image = card.querySelector('[data-result-image]');
        primaryLink.href = product.url;
        cta.href = product.url;
        image.src = product.image;
        image.alt = product.title;
        card.querySelector('[data-result-rank]').textContent = interpolate(this.config.rankLabel, {
          rank: product.rank,
        });
        card.querySelector('[data-result-title]').textContent = product.title;
        card.querySelector('[data-result-price]').textContent = formatter.format(product.price / 100);

        const reasonList = card.querySelector('[data-result-reasons]');
        for (const reason of product.reasons.slice(0, 3)) {
          const item = document.createElement('li');
          item.textContent = this.answerLabels.get(reason.value) ?? reason.value;
          reasonList.append(item);
        }

        for (const link of [primaryLink, cta]) {
          link.addEventListener('click', () => emitFinderEvent(
            'scent_finder_product_click',
            { handle: product.handle, rank: product.rank },
            window.dataLayer,
          ));
        }
        this.resultGrid.append(card);
      }

      this.loading.hidden = true;
      this.results.hidden = false;
      this.status.textContent = interpolate(this.config.resultStatus, { count: products.length });
      this.results.querySelector('h2')?.focus();
      emitFinderEvent(
        'scent_finder_result_view',
        { handles: products.map((product) => product.handle) },
        window.dataLayer,
      );
    }

    retake() {
      emitFinderEvent('scent_finder_retake', {}, window.dataLayer);
      this.form.reset();
      this.answers = {
        identity: '',
        families: [],
        character: '',
        occasion: '',
        season: '',
      };
      this.started = false;
      this.results.hidden = true;
      this.error.hidden = true;
      this.loading.hidden = true;
      this.form.hidden = false;
      this.progress.hidden = false;
      this.showStep(0);
    }
  }

  window.customElements.define('scent-finder', NicheScentFinder);
}
